'use client';

import { ChevronsUpDown, CreditCard, LogOut, Settings } from 'lucide-react';
import { UserAvatar } from '@ui/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@ui/components/ui/dropdown-menu';
import { SidebarMenuButton, SidebarMenuItem, useSidebar } from '@ui/components/ui/sidebar';
import { createClient } from '@/lib/supabase/client';
import { useRouter } from 'next/navigation';
import { useUser } from '@/contexts/user-context';
import { useTranslations } from 'next-intl';
import { createCustomerPortalSession } from '@/lib/payment/stripe';
import { useApp } from '@/contexts/app-context';
import { toast } from '@ui/components/ui/sonner';
import { datadogRum } from '@datadog/browser-rum';

export function NavUser() {
  const { user } = useUser();
  const { loading: isPending, startTransition } = useApp();
  const { isMobile, toggleSidebar } = useSidebar();
  const router = useRouter();
  const t = useTranslations('Navigation');
  const tPricing = useTranslations('Pricing');

  const signOut = async () => {
    const supabase = createClient();
    await supabase.auth.signOut();
    startTransition(() => {
      router.push('/auth/login');
    });
  };

  return (
    <SidebarMenuItem>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton size="lg" className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">
            <UserAvatar user={user} />
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span data-testid="user-fullname" className="truncate font-medium">
                {user.fullName}
              </span>
              <span className="truncate text-xs">
                {user.subscriptionName} {t('plan')}
              </span>
            </div>
            <ChevronsUpDown className="ml-auto size-4" />
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
          side={isMobile ? 'bottom' : 'right'}
          align="end"
          sideOffset={4}
        >
          <DropdownMenuLabel className="p-0 font-normal">
            <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
              <UserAvatar user={user} />
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{user.fullName}</span>
                <span className="truncate text-xs">
                  {user.subscriptionName} {t('plan')}
                </span>
              </div>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuItem
              onClick={() =>
                startTransition(() => {
                  if (isMobile) toggleSidebar();
                  router.push('/account');
                })
              }
              disabled={isPending}
            >
              <Settings />
              {t('accountSettings')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() =>
                startTransition(async () => {
                  if (isMobile) toggleSidebar();
                  if (user.isExternallyManaged) {
                    toast.info(tPricing('partnerManagedBilling'));
                    return;
                  }
                  await createCustomerPortalSession();
                })
              }
              disabled={isPending}
            >
              <CreditCard />
              {t('billing')}
            </DropdownMenuItem>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() =>
              startTransition(() => {
                if (isMobile) toggleSidebar();
                datadogRum.stopSessionReplayRecording();
                datadogRum.stopSession();
                signOut();
              })
            }
            disabled={isPending}
          >
            <LogOut />
            {t('logOut')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
}
